// Developer     : Magalí Perea
// Creation Date : 11-3-2024
// Version       : 2
// Description   : Diálogo para modificar reclamos (detalle, motivo, encargado).

import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Typography } from "@mui/material";
import Notify from "../../../components//Notify";
import { getData, putData } from "../../../services/library";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";
import { logEventos } from "../../../services/services";

function EditarReclamo(props) {
  const [reclamo, setReclamo] = useState(props.datos);
  const [motivoReclamo, setMotivoReclamo] = useState(null);
  const [dirReclamo, setDirReclamo] = useState(null);
  const [disBtn, setDisBtn] = useState(false);

  const handleClose = () => {
    props.setOpen(false);
  };

  const [notificacion, setNotificacion] = useState({
    open: false,
    mensaje: "",
    severity: "",
    color: "",
  });
  const [motivosEmergencias, setMotivosEmergencias] = useState();
  const [prioridades, setPrioridades] = useState();
  const [dirsEntrega, setDirsEntrega] = useState();
  const [encargados, setEncargados] = useState();

  // Fix #4: mounted flag para evitar setear estado en componente desmontado
  useEffect(() => {
    let mounted = true;
    const safeSet = (setter) => (data) => {
      if (mounted) setter(data);
    };

    getData(
      "/motivos_emergencias/activos/sinDesconexion",
      safeSet(setMotivosEmergencias)
    );
    getData("/desplegables/reclamos", safeSet(setPrioridades));
    getData("/desplegables/reclamos_cargo", safeSet(setEncargados));
    getData(
      "/direccionesentrega/cli/" + reclamo.id_cliente,
      safeSet(setDirsEntrega)
    );

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!!motivosEmergencias) {
      const motivo = motivosEmergencias.filter(
        (motivo) => motivo.id_motivo === reclamo.id_motivo
      );
      setMotivoReclamo(motivo[0] ?? null);
    }
  }, [motivosEmergencias]);

  useEffect(() => {
    if (!!dirsEntrega) {
      const direccion = dirsEntrega.filter(
        (dir) => dir.id === reclamo.direccion_entrega
      );
      setDirReclamo(direccion[0] ?? null);
    }
  }, [dirsEntrega]);

  async function modificarReclamo(e) {
    // Fix #7 + #3: validación manual antes de enviar
    if (!motivoReclamo || !dirReclamo) {
      setNotificacion({
        open: true,
        mensaje: "Por favor complete todos los campos requeridos.",
        severity: "warning",
        color: "#FDEBD0",
      });
      return;
    }

    setDisBtn(true);
    try {
      // Fix #1: await putData para no llamar actualizar antes de que termine
      await putData(
        e,
        "/reclamos/" + reclamo.idreclamo,
        {
          detalle: reclamo.detalle,
          id_motivo: motivoReclamo.id_motivo,
          prioridad: reclamo.prioridad,
          direccion_entrega: dirReclamo.id,
          a_cargo: reclamo.a_cargo,
          usuario_modifica: localStorage.getItem("user"),
          estado: reclamo.estado, // Fix #9: estado del state local, no de props
        },
        setNotificacion
      );
      // Fix #5: logEventos solo si el PUT fue exitoso
      logEventos(
        "Guardar edición de reclamo",
        "Reclamos Granel",
        "Se guardó la edición del reclamo",
        localStorage.getItem("user")
      );
    } catch {
      // Fix #2: resetear disBtn para permitir reintentar o cancelar
      setDisBtn(false);
    }
  }

  const almacenar = (e) => {
    setReclamo({ ...reclamo, [e.target.name]: e.target.value });
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        {notificacion && notificacion.open ? (
          <Notify
            mensaje={notificacion.mensaje}
            open={notificacion.open}
            color={notificacion.color}
            handleClose={() => {
              // Fix #6: actualizar y cerrar solo en éxito, no en error/warning
              if (notificacion.severity === "success") {
                props.actualizar();
                props.setOpen(false);
              }
              setDisBtn(false);
              setNotificacion({ open: false });
            }}
            severity={notificacion.severity}
          />
        ) : null}
        <form onSubmit={modificarReclamo}>
          <Box backgroundColor={"#1F618D"}>
            <DialogTitle>
              <Typography color="#ffffff" fontSize={20}>
                Modificar reclamo N° {reclamo.idreclamo}
              </Typography>
            </DialogTitle>
          </Box>
          <DialogContent>
            <Grid
              container
              spacing={1}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={6}>
                {!!motivosEmergencias && (
                  <Autocomplete
                    disabled={
                      reclamo.estado === 171 ||
                      reclamo.estado === 223 ||
                      reclamo.categoria === 281 ||
                      reclamo.categoria === 282
                    }
                    fullWidth
                    disableClearable
                    onChange={(event, value) => {
                      setMotivoReclamo(value);
                    }}
                    noOptionsText={"Motivo no registrado"}
                    options={motivosEmergencias}
                    getOptionLabel={(option) =>
                      option?.descripcion_motivo || ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id_motivo === value.id_motivo
                    }
                    value={motivoReclamo}
                    renderOption={(props, option) => (
                      // Fix #10: key después de spread para evitar conflicto con MUI
                      <Box
                        component="li"
                        {...props}
                        key={option.id_motivo}
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      >
                        {option.descripcion_motivo}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        placeholder="Tipee para buscar motivo..."
                        {...params}
                        label="Motivo"
                        inputProps={{
                          ...params.inputProps,
                        }}
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={6}>
                {prioridades && (
                  <FormControl required fullWidth disabled>
                    <InputLabel> Prioridad</InputLabel>
                    <Select
                      label="Prioridad"
                      name="prioridad"
                      value={reclamo.prioridad}
                      onChange={almacenar}
                    >
                      {prioridades
                        ? prioridades.map((elemento) => (
                            <MenuItem key={elemento.id} value={elemento.id}>
                              {elemento.valor}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={9}>
                {dirsEntrega && (
                  <Autocomplete
                    disabled
                    required
                    fullWidth
                    onChange={(event, value) => {
                      setDirReclamo(value);
                    }}
                    noOptionsText={"Dirección no registrada"}
                    options={dirsEntrega}
                    getOptionLabel={(option) => option?.direccion_entrega || ""}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    value={dirReclamo}
                    renderOption={(props, option) => (
                      // Fix #10: key después de spread
                      <Box
                        component="li"
                        {...props}
                        key={option.id}
                        sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                      >
                        <b>{option.direccion_entrega} </b> -{" "}
                        {option.nombre_direccion}
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        placeholder="Tipee para buscar dirección..."
                        {...params}
                        label="Dirección"
                        inputProps={{
                          ...params.inputProps,
                        }}
                      />
                    )}
                  />
                )}
              </Grid>

              <Grid item xs={3}>
                {encargados && (
                  <FormControl fullWidth>
                    <InputLabel>A cargo de:</InputLabel>
                    <Select
                      label="A cargo de: "
                      name="a_cargo"
                      value={reclamo.a_cargo}
                      onChange={almacenar}
                    >
                      {encargados
                        ? encargados.map((elemento) => (
                            <MenuItem key={elemento.id} value={elemento.id}>
                              {elemento.valor}
                            </MenuItem>
                          ))
                        : null}
                    </Select>
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Detalle"
                  name="detalle"
                  value={reclamo.detalle}
                  onChange={almacenar}
                  margin="dense"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
            <br></br>
          </DialogContent>
          <DialogActions>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignContent="center"
            >
              <Button
                color="error"
                disabled={disBtn}
                variant="contained"
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                color="success"
                type="submit"
                variant="contained"
                disabled={disBtn}
              >
                Confirmar
              </Button>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}

export default EditarReclamo;
